import { defineStore } from 'pinia'
import api from '@/services/api'

const CACHE_TTL_MS = 60 * 60 * 1000 // 1 hora

export const useMetadataListasStore = defineStore('metadataListas', {
  state: () => ({
    mapeamentos: [],
    listasCache: {}, // Cache para armazenar os itens das listas carregadas { cacheKey: [ { id, textoLista }, ... ] }
    cacheTimestamps: {}, // Timestamps de quando cada cache foi armazenado { cacheKey: Date.now() }
    isLoading: false,
    loadingPromise: null
  }),

  actions: {
    /**
     * Verifica se uma chave de cache ainda está dentro do TTL
     */
    _isCacheValido (cacheKey) {
      const timestamp = this.cacheTimestamps[cacheKey]
      if (!timestamp) return false
      return (Date.now() - timestamp) < CACHE_TTL_MS
    },

    /**
     * Carrega todos os mapeamentos de campos/listas do banco Master
     */
    async carregarMetadados () {
      if (this.mapeamentos && this.mapeamentos.length > 0) return
      if (this.loadingPromise) return this.loadingPromise

      this.isLoading = true
      this.loadingPromise = (async () => {
        try {
          const res = await api.get('/DicionarioMetadadosListas')
          this.mapeamentos = Array.isArray(res.data) ? res.data : []
        } catch (e) {
          console.error('Erro ao carregar metadados de listas:', e)
          this.mapeamentos = []
        } finally {
          this.isLoading = false
          this.loadingPromise = null
        }
      })()

      return this.loadingPromise
    },

    /**
     * Retorna o mapeamento específico para um campo de uma tabela
     */
    getMapping (tabela, campo) {
      if (!tabela || !campo || !Array.isArray(this.mapeamentos)) return null

      return this.mapeamentos.find(m =>
        m.tabelaNome?.toLowerCase() === tabela.toLowerCase() &&
        m.campoNome?.toLowerCase() === campo.toLowerCase()
      )
    },

    /**
     * Busca uma lista de lookup dinamicamente e armazena em cache com TTL de 1 hora
     * @param {string} codigo Código da lista (ex: MOE, UN_MED)
     * @param {string} tipo "Sistema" ou "Cliente"
     * @param {boolean} ordenarPorTexto Se true, retorna a lista ordenada por textoLista
     * @param {string|null} idioma Idioma para filtrar (ex: 'en-US'). Quando informado, passa ?idioma= ao endpoint.
     */
    async getListaLookup (codigo, tipo, ordenarPorTexto = false, idioma = null) {
      if (!codigo) return []

      const cacheKey = idioma ? `${tipo}_${codigo}_${idioma}` : `${tipo}_${codigo}`
      const cacheValido = this._isCacheValido(cacheKey)

      if (cacheValido && this.listasCache[cacheKey]) {
        const lista = this.listasCache[cacheKey]
        if (ordenarPorTexto && Array.isArray(lista)) {
          return [...lista].sort((a, b) => (a.textoLista || '').localeCompare(b.textoLista || ''))
        }
        return lista
      }

      const baseEndpoint = tipo?.toLowerCase() === 'sistema' ? '/ListasSistema' : '/ListasCliente'

      try {
        let url = `${baseEndpoint}/lookup/${codigo}`
        if (idioma) url += `?idioma=${encodeURIComponent(idioma)}`
        const res = await api.get(url)
        const lista = res.data
        this.listasCache[cacheKey] = lista
        this.cacheTimestamps[cacheKey] = Date.now()
        return lista
      } catch (e) {
        console.error(`Erro ao carregar lista de lookup ${codigo} (${tipo}):`, e)
        return []
      }
    },

    /**
     * Busca a lista de lookup baseada no mapeamento de tabela e campo
     * @param {string} tabela Nome da tabela no banco
     * @param {string} campo Nome do campo no banco
     * @param {boolean} ordenarPorTexto Se true, retorna a lista ordenada por textoLista
     */
    async getListaPorCampo (tabela, campo, ordenarPorTexto = false) {
      await this.carregarMetadados()
      const mapping = this.getMapping(tabela, campo)
      if (!mapping) {
        console.warn(`Nenhum mapeamento de metadados encontrado para ${tabela}.${campo}`)
        return []
      }
      return await this.getListaLookup(mapping.codigoLista, mapping.tipoLista, ordenarPorTexto)
    },

    /**
     * Busca os mapeamentos para tabelas específicas e retorna um objeto com as listas carregadas
     * @param {Array<string>} tabelasNomes Array de nomes de tabelas (ex: ['ContratoCompra', 'ContratoProduto'])
     * @param {boolean} ordenarPorTexto Se true, retorna as listas ordenadas por textoLista
     */
    async carregarListasParaTabelas (tabelasNomes, ordenarPorTexto = false) {
      await this.carregarMetadados()

      const tabelasSet = new Set(tabelasNomes.map(t => t.toLowerCase()))
      const mappingsRelevantes = this.mapeamentos.filter(m => tabelasSet.has(m.tabelaNome.toLowerCase()))

      const resultado = {}
      const promises = []

      for (const m of mappingsRelevantes) {
        if (m.campoNome && !resultado[m.campoNome]) {
          const promise = this.getListaLookup(m.codigoLista, m.tipoLista, ordenarPorTexto)
            .then(lista => {
              resultado[m.campoNome] = lista
            })
          promises.push(promise)
        }
      }

      await Promise.all(promises)
      return resultado
    }
  }
})
